import { create } from 'zustand';
import { friendsAPI } from '../lib/api';
import { getChannel, joinUserChannel } from '../lib/realtime';

interface Friend {
  id: string;
  user_id: string;
  friend_id: string;
  status: string;
  created_at: string;
  friend?: {
    username: string;
    display_name?: string | null;
    avatar_url: string | null;
    status: string | null;
  };
  user?: {
    username: string;
    display_name?: string | null;
    avatar_url: string | null;
    status?: string | null;
  };
}

interface FriendState {
  friends: Friend[];
  pendingRequests: Friend[];
  isLoading: boolean;
  error: string | null;
  
  fetchFriends: () => Promise<void>;
  fetchPendingRequests: () => Promise<void>;
  sendFriendRequest: (username: string) => Promise<void>;
  acceptFriendRequest: (requestId: string) => Promise<void>;
  rejectFriendRequest: (requestId: string) => Promise<void>;
  removeFriend: (friendId: string) => Promise<void>;
  subscribeToFriendRequests: () => () => void;
}

export const useFriendStore = create<FriendState>((set, get) => ({
  friends: [],
  pendingRequests: [],
  isLoading: false,
  error: null,
  
  fetchFriends: async () => {
    try {
      set({ isLoading: true, error: null });
      
      // Get accepted friends from API
      const raw = await friendsAPI.getFriends();
      // Normalize: Go returns "profile" but store expects "friend"
      const friends = (raw || []).map((f: any) => ({
        ...f,
        friend: f.friend || f.profile || null,
      }));
      
      set({ friends });
    } catch (error: any) {
      console.error('Error fetching friends:', error);
      set({ error: error.response?.data?.message || error.message || 'Failed to fetch friends' });
    } finally {
      set({ isLoading: false });
    }
  },
  
  fetchPendingRequests: async () => {
    try {
      set({ isLoading: true, error: null });
      
      // Get pending friend requests from API
      const raw = await friendsAPI.getPendingRequests();
      const pendingRequests = (raw || []).map((f: any) => ({
        ...f,
        friend: f.friend || f.profile || null,
        user: f.user || f.profile || f.friend || null,
      }));
      
      set({ pendingRequests });
    } catch (error: any) {
      console.error('Error fetching pending requests:', error);
      set({ error: error.response?.data?.message || error.message || 'Failed to fetch pending requests' });
    } finally {
      set({ isLoading: false });
    }
  },
  
  sendFriendRequest: async (username) => {
    try {
      set({ isLoading: true, error: null });
      
      // Send friend request with API
      await friendsAPI.sendFriendRequest(username);
      
      // Refresh pending requests
      await get().fetchPendingRequests();
    } catch (error: any) {
      console.error('Error sending friend request:', error);
      set({ error: error.response?.data?.message || error.message || 'Failed to send friend request' });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },
  
  acceptFriendRequest: async (requestId) => {
    try {
      set({ isLoading: true, error: null });
      
      // Accept friend request with API
      await friendsAPI.acceptFriendRequest(requestId);
      
      // Refresh lists
      await get().fetchFriends();
      await get().fetchPendingRequests();
    } catch (error: any) {
      console.error('Error accepting friend request:', error);
      set({ error: error.response?.data?.message || error.message || 'Failed to accept friend request' });
    } finally {
      set({ isLoading: false });
    }
  },
  
  rejectFriendRequest: async (requestId) => {
    try {
      set({ isLoading: true, error: null });
      
      // Reject friend request with API
      await friendsAPI.rejectFriendRequest(requestId);
      
      // Refresh pending requests
      await get().fetchPendingRequests();
    } catch (error: any) {
      console.error('Error rejecting friend request:', error);
      set({ error: error.response?.data?.message || error.message || 'Failed to reject friend request' });
    } finally {
      set({ isLoading: false });
    }
  },
  
  removeFriend: async (friendId) => {
    try {
      set({ isLoading: true, error: null });
      
      // Remove friend with API
      await friendsAPI.removeFriend(friendId);
      
      // Refresh friends list
      await get().fetchFriends();
    } catch (error: any) {
      console.error('Error removing friend:', error);
      set({ error: error.response?.data?.message || error.message || 'Failed to remove friend' });
    } finally {
      set({ isLoading: false });
    }
  },
  
  subscribeToFriendRequests: () => {
    const userId = localStorage.getItem('user_id');
    if (!userId) return () => {};
    
    joinUserChannel(userId);
    const channel = getChannel(`user:${userId}`);
    if (!channel) return () => {};
    
    // Friend events come via the user channel
    const requestRef = channel.on('friend_request', () => {
      get().fetchPendingRequests();
    });
    
    const acceptRef = channel.on('friend_accepted', () => {
      get().fetchFriends();
      get().fetchPendingRequests();
    });
    
    const removeRef = channel.on('friend_removed', () => {
      get().fetchFriends();
    });

    // Keep statuses fresh even when no explicit friend events are emitted.
    const refreshTimer = window.setInterval(() => {
      get().fetchFriends();
    }, 15000);
    
    // Return cleanup function
    return () => {
      channel.off('friend_request', requestRef);
      channel.off('friend_accepted', acceptRef);
      channel.off('friend_removed', removeRef);
      window.clearInterval(refreshTimer);
    };
  }
}));

// Initialize by fetching friends when token is available
if (localStorage.getItem('access_token')) {
  useFriendStore.getState().fetchFriends();
  useFriendStore.getState().fetchPendingRequests();
}